<template>
  <div class="create-page">
    <div class="page-header">
      <h1>Agregar usuario</h1>
      <router-link to="/" class="btn btn-secondary">Volver</router-link>
    </div>

    <div v-if="createdUser" class="success-card">
      <h2>Usuario creado</h2>
      <div class="created-info">
        <div class="info-row">
          <span class="info-label">Email:</span>
          <span>{{ createdUser.email }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Nombre:</span>
          <span>{{ createdUser.name }}</span>
        </div>
        <div class="info-row password-row">
          <span class="info-label">Contraseña:</span>
          <code class="password-value">{{ createdUser.password }}</code>
        </div>
      </div>
      <p class="password-warning">Esta contraseña solo se mostrará una vez. Cópiala ahora.</p>
      <router-link to="/" class="btn btn-primary">Volver a usuarios</router-link>
    </div>

    <form v-else class="create-form" @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="email">Email</label>
        <input
          id="email"
          v-model="form.email"
          type="email"
          required
          placeholder="usuario@ejemplo.com"
        />
      </div>

      <div class="form-group">
        <label for="name">Nombre</label>
        <input
          id="name"
          v-model="form.name"
          type="text"
          required
          placeholder="Nombre completo"
        />
      </div>

      <div class="form-group">
        <label for="profile">Perfil</label>
        <select id="profile" v-model="form.profileId" required>
          <option value="" disabled>Seleccionar perfil</option>
          <option v-for="profile in profiles" :key="profile.id" :value="profile.id">
            {{ profile.name }}
          </option>
        </select>
      </div>

      <p v-if="error" class="error-message">{{ error }}</p>

      <div class="form-actions">
        <router-link to="/" class="btn btn-secondary">Cancelar</router-link>
        <button type="submit" class="btn btn-primary" :disabled="submitting">
          {{ submitting ? 'Creando...' : 'Crear usuario' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import usersApi, { type UserProfile, type CreatedUser } from '../api/users';

const profiles = ref<UserProfile[]>([]);
const createdUser = ref<CreatedUser | null>(null);
const submitting = ref(false);
const error = ref('');

const form = reactive({
  email: '',
  name: '',
  profileId: '' as number | '',
});

const loadProfiles = async () => {
  try {
    const response = await usersApi.getProfiles();
    profiles.value = response.data.profiles;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Error al cargar perfiles';
  }
};

const handleSubmit = async () => {
  if (!form.profileId) return;

  submitting.value = true;
  error.value = '';
  try {
    const response = await usersApi.createUser({
      email: form.email,
      name: form.name,
      profileId: form.profileId as number,
    });
    createdUser.value = response.data.user;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Error al crear usuario';
  } finally {
    submitting.value = false;
  }
};

onMounted(loadProfiles);
</script>

<style scoped>
.create-page {
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  color: #e0e0e0;
  margin: 0;
}

.create-form {
  background-color: #1a1a1a;
  border-radius: 12px;
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: #b0b0b0;
  font-size: 14px;
  margin-bottom: 6px;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 14px;
  background-color: #0d0d0d;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-group input::placeholder {
  color: #666;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #4a90d9;
}

.form-group select {
  cursor: pointer;
}

.form-group select option {
  background-color: #1a1a1a;
  color: #e0e0e0;
}

.error-message {
  color: #ff6b6b;
  font-size: 14px;
  margin: 0 0 16px 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.btn-primary {
  background-color: #4a90d9;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #3a7bc8;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #2a2a2a;
  color: #b0b0b0;
  border: 1px solid #3a3a3a;
}

.btn-secondary:hover {
  background-color: #3a3a3a;
  color: #e0e0e0;
}

.success-card {
  background-color: #1a1a1a;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
}

.success-card h2 {
  color: #4a90d9;
  margin: 0 0 20px 0;
}

.created-info {
  text-align: left;
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  padding: 8px 0;
  color: #e0e0e0;
  font-size: 14px;
}

.info-label {
  color: #b0b0b0;
  min-width: 100px;
}

.password-row {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #2a2a2a;
}

.password-value {
  background-color: #0d0d0d;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 16px;
  letter-spacing: 1px;
  color: #4a90d9;
  user-select: all;
}

.password-warning {
  color: #ff6b6b;
  font-size: 13px;
  margin: 0 0 20px 0;
}
</style>
